/* eslint-disable no-case-declarations */
/* eslint-disable import/no-cycle */
/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2020, Bulb Inc.  For more information, please email contact@hellobulb.com.
 *                _
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
 */
import {
  Component, ComponentFactoryResolver, Inject, Input, OnDestroy, OnInit, ViewChild, ViewContainerRef,
} from '@angular/core';

import { Subscription } from 'rxjs';

import { TRANSLOCO_SCOPE, TranslocoService } from '@jsverse/transloco';
import { availableLangs } from 'src/app/exports';
import { AeroDialogService } from 'src/aero/aero-dialog/aero-dialog.service';
import { BULB_BUSY_COVER_SERVICE } from 'src/app/angularjs.providers';
import { HttpClient } from '@angular/common/http';
import { Resume } from './resume.model';
import { ResumeCustomSubSection } from './custom-section/custom-sub-section.model';
import { ResumeSection } from './section/section.model';
import { ResumeSectionType } from './section/resume-section-type.enum';
import { ResumeService } from './resume.service';
import { ResumeAboutMeComponent } from './about-me/about-me.component';
import { ResumeExperienceComponent } from './experience/experience.component';
import { ResumeEducationComponent } from './education/education.component';
import { ResumeCustomSectionComponent } from './custom-section/custom-section.component';

const loader = availableLangs.reduce((acc, lang) => {
  acc[lang] = () => import(`src/locale/messages.${lang}.json`);
  return acc;
}, {});

/**
 * Component that houses the Resumé connected to the current account.
 */
@Component({
  selector: 'bulb-resume',
  templateUrl: './resume.component.html',
  providers: [
    { provide: TRANSLOCO_SCOPE, useValue: { scope: '', loader } },
  ],
  standalone: false,
})
export class ResumeComponent implements OnDestroy, OnInit {
  /**
   * Reference to the owner of the resume.
   */
  @Input() user: any;

  /**
   * Reference to the resume model.
   */
  public resume: Resume;

  /**
   * value of screen width to trigger mobile view alert
   */
  public screenWidth: number = 1280;

  /**
   * Whether the current user can edit the resume.
   */
  public canEdit: boolean = false;

  /**
   * Subscription of the resume name.
   */
  private resumeNameSubscription: Subscription;

  /**
   * The number of subSections that have portfolio content which have called #shouldSubsectionBeOpen
   * @private
   */
  private subSectionsWithPortfolioContentCount = 0;

  /**
   * Dynamic component reference.
   */
  @ViewChild('comps', { read: ViewContainerRef }) comps: ViewContainerRef;

  /**
   * Contains data of drag start accordian.
   */
  dragStartSection: any;

  /**
   * Contains data of section on which accordian was dragged.
   */
  dragEndSection: any;

  /**
   *  Contains dropdown items of add a section button.
   */
  createMenuItems: any;

  /**
   * Hide and show custom dropdown button.
   */
  showCustomButtonDropdown: boolean;

  /**
   * Default constructor.
   */
  constructor(
    private aeroDialogService : AeroDialogService,
    private componentFactoryResolver: ComponentFactoryResolver,
    @Inject('BulbAuthService') private bulbAuthService,
    @Inject(BULB_BUSY_COVER_SERVICE) private bulbBusyCoverService,
    private resumeService : ResumeService,
    private http: HttpClient,
    private translocoService : TranslocoService,
    @Inject('$state') private $state,
  ) {}

  /**
   * A lifecycle hook that is called after Angular has initialized all data-bound properties of a directive.
   */
  public ngOnInit() : void {
    this.updateResumeName();

    if (!this.user.resumeId) {
      // there isn't a resume for the author yet.
      if (this.user.id === this.bulbAuthService.currentUser.id) {
        // Send REST call to create the resume:
        this.resumeService.createResume().subscribe((resume) => {
          // Once we create the resume, we need to re-call fetchCurrentUser so that all locations in the app
          // will have access to resumeId on the currentUser
          this.bulbAuthService.fetchCurrentUser();
          this.updateResume(resume);
          setTimeout(() => {
            this.addComponent();
          });
          this.user.resumeId = resume.id;
        });
      } else {
        // If the currentUser isn't the author, we shouldn't be here...
        this.$state.go('accessDenied', {}, { location: false });
      }
    } else {
      // fetch the resume if we have resume id.
      this.bulbBusyCoverService.showBusyDuring(
        this.resumeService.fetchResume(this.user.resumeId).then((resume) => {
          this.updateResume(resume);
          setTimeout(() => {
            this.addComponent();
          });
          this.resumeService.canViewResume = true;
        }).catch(() => {
          this.resumeService.canViewResume = false;
        }),
      );
    }
  }

  /**
   * Check the state of specialized sections.
   */
  checkSpecializedSection(sectionName) {
    return this.resume.sections.some((sections) => sections.type === sectionName);
  }

  /**
   * Checks dropdown button visibility.
   */
  addSectionDropdown() {
    const isEducation = this.resume.sections.some((sections) => sections.type === 'Education');
    const isExperience = this.resume.sections.some((sections) => sections.type === 'Experience');
    return (isEducation && isExperience);
  }

  /**
   * Add specialized section about me, experience, education.
   */
  addSpecializedSection(section) {
    const customSection: ResumeSection = new ResumeSection({
      type: ResumeSectionType[section],
      empty: true,
    });
    this.resume.sections.push(customSection);
    this.save().then((resume) => {
      this.updateResume(resume);
      this.addComponent(true);
    });
  }

  /**
   * List of menu items for add a section button.
   */
  createMenuItem() {
    this.createMenuItems = [
      {
        id: 'general',
        label: this.translocoService.translate('resume.general.section'),
        closeMenuOnClick: true,
        command: () => {
          this.addNewCustomSection();
        },
      },
      {
        id: 'Experience',
        label: this.translocoService.translate('resume.experience.section'),
        closeMenuOnClick: true,
        hidden: this.checkSpecializedSection('Experience'),
        command: () => {
          this.addSpecializedSection('Experience');
        },
      },
      {
        id: 'Education',
        label: this.translocoService.translate('resume.education.section'),
        closeMenuOnClick: true,
        hidden: this.checkSpecializedSection('Education'),
        command: () => {
          this.addSpecializedSection('Education');
        },
      },
    ];
  }

  /**
   * Add resume components dynamically.
   * @param tabChange Gives information to store tab session.
   */
  addComponent(tabChange ?:boolean) {
    this.createMenuItem();
    this.showCustomButtonDropdown = this.addSectionDropdown();
    this.comps?.clear();
    const resumeCategory = this.resume.sections;
    if (this.comps) {
      for (let i = 0; i <= resumeCategory.length; i++) {
        let aComponentFactory;
        let aComponentRef;
        // eslint-disable-next-line default-case
        switch (resumeCategory[i]?.type) {
          case 'AboutMe':
            aComponentFactory = this.componentFactoryResolver.resolveComponentFactory(ResumeAboutMeComponent);
            aComponentRef = this.comps.createComponent(aComponentFactory);
            (<ResumeAboutMeComponent>aComponentRef.instance).resumeComponent = this;
            (<ResumeAboutMeComponent>aComponentRef.instance).aboutMeSection = this.resume.sections[i];
            (<ResumeAboutMeComponent>aComponentRef.instance).canEdit = this.canEdit;
            (<ResumeAboutMeComponent>aComponentRef.instance).tabChange = tabChange;
            break;
          case 'Experience':
            aComponentFactory = this.componentFactoryResolver.resolveComponentFactory(ResumeExperienceComponent);
            aComponentRef = this.comps.createComponent(aComponentFactory);
            (<ResumeExperienceComponent>aComponentRef.instance).resumeComponent = this;
            (<ResumeExperienceComponent>aComponentRef.instance).experienceSection = this.resume.sections[i];
            (<ResumeExperienceComponent>aComponentRef.instance).canEdit = this.canEdit;
            (<ResumeExperienceComponent>aComponentRef.instance).tabChange = tabChange;
            break;
          case 'Education':
            aComponentFactory = this.componentFactoryResolver.resolveComponentFactory(ResumeEducationComponent);
            aComponentRef = this.comps.createComponent(aComponentFactory);
            (<ResumeEducationComponent>aComponentRef.instance).resumeComponent = this;
            (<ResumeEducationComponent>aComponentRef.instance).educationSection = this.resume.sections[i];
            (<ResumeEducationComponent>aComponentRef.instance).canEdit = this.canEdit;
            (<ResumeEducationComponent>aComponentRef.instance).tabChange = tabChange;
            break;
          case 'Custom':
            aComponentFactory = this.componentFactoryResolver.resolveComponentFactory(ResumeCustomSectionComponent);
            aComponentRef = this.comps.createComponent(aComponentFactory);
            (<ResumeCustomSectionComponent>aComponentRef.instance).resumeComponent = this;
            (<ResumeCustomSectionComponent>aComponentRef.instance).customSection = this.resume.sections[i];
            (<ResumeCustomSectionComponent>aComponentRef.instance).canEdit = this.canEdit;
            (<ResumeCustomSectionComponent>aComponentRef.instance).tabChange = tabChange;
            (<ResumeCustomSectionComponent>aComponentRef.instance).customTabOpen = this.resume.sections[i].id;
            break;
        }
      }
    }
  }

  public save() : Promise < any > {
    return this.http.put(`resumes/${this.resume.id}`, this.resume).toPromise();
  }

  /**
   * Method to add the new custom section to the resumé view
   */
  public addNewCustomSection() {
    // Retrieve the last custom section.
    const section = this.resume.sections.slice(-1)[0];
    // if the last custom section is newly added, then do nothing.
    if (section?.type === ResumeSectionType.Custom && section?.empty) {
      return;
    }

    const customSection: ResumeSection = new ResumeSection({
      type: ResumeSectionType.Custom,
      empty: true,
    });
    customSection.subSections.push(new ResumeCustomSubSection());
    this.resume.sections.push(customSection);
    this.addComponent(true);
  }

  /**
   * Method to allow the very first sub-section with portfolio content be open by default.
   * This will return true to the first caller.
   */
  public shouldSubSectionBeOpen() {
    this.subSectionsWithPortfolioContentCount += 1;
    return (this.subSectionsWithPortfolioContentCount === 1);
  }

  /**
   * Update the resume object.
   */
  public updateResume(resume: Resume) {
    this.resume = resume;
    this.canEdit = this.bulbAuthService.currentUser.hasPermission(resume, 'UPDATE');
  }

  /**
   * Update the resume name.
   */
  updateResumeName() {
    this.resumeNameSubscription = this.resumeService.updateResumeName.subscribe((fullName: string) => {
      this.resume.fullName = fullName;
    });
  }

  /**
   * Method to check, if any subsection has logo
   * @private
   */
  public checkIsLogoUploaded(subsectionList) {
    let isLogoUploaded: boolean = false;
    subsectionList.forEach((section) => {
      if (section.logo != null) {
        isLogoUploaded = true;
      }
    });
    return isLogoUploaded;
  }

  /**
   * Drag start event.
   * @param dragStartSection  Dragged section
   */
  dragStart(dragStartSection) {
    this.dragStartSection = dragStartSection;
  }

  /**
   * Drop event
   * @param dragEndSection Dragged over section.
   */
  drop(dragEndSection) {
    this.dragEndSection = dragEndSection;
    const startIndex = this.resume.sections.indexOf(this.dragStartSection);
    if (dragEndSection === 'dragLast') {
      this.resume.sections.push(this.resume.sections.splice(startIndex, 1)[0]);
      this.addComponent(true);
      this.save();
    } else {
      const endIndex = this.resume.sections.indexOf(this.dragEndSection);
      this.shiftSection(this.resume.sections, startIndex, endIndex);
    }
  }

  /**
   * shift resume sections.
   * @param sections Contains resume sections.
   * @param old_index start index of dragging section.
   * @param new_index Index of section on which it is dragged.
   */
  shiftSection(sections, old_index, new_index) {
    if (old_index >= new_index) {
      sections.splice(new_index, 0, sections.splice(old_index, 1)[0]);
    } else {
      sections.splice(new_index - 1, 0, sections.splice(old_index, 1)[0]);
    }
    this.addComponent(true);
    this.save();
  }

  /**
   * A lifecycle hook that is called when a directive, pipe, or service is destroyed.
   * Use for any custom cleanup that needs to occur when the instance is destroyed.
   */
  ngOnDestroy() {
    if (this.resumeNameSubscription) {
      this.resumeNameSubscription.unsubscribe();
    }
  }
}
